/**
 * Widget styles.
 *
 * Theme rozhoduje **klient** v tenant config (Settings → Support → Widget):
 *   - 'light' (default) — bílé pozadí, černý text
 *   - 'dark'            — tmavé pozadí, světlý text
 *   - 'auto'            — respekt prefers-color-scheme (OS visitora)
 *
 * Klient typicky volí dle svého webu (Obalero light, hypotetický dark dashboard
 * by dal 'dark'). 'auto' jen pro multi-theme weby co samy přepínají.
 */
const LIGHT_TOKENS = `
  --mw-bg: #ffffff;
  --mw-text: #0f1923;
  --mw-text-muted: #6b7280;
  --mw-border: #e5e7eb;
  --mw-bubble-in: #f3f4f6;`

const DARK_TOKENS = `
  --mw-bg: #1a2736;
  --mw-text: #e8edf2;
  --mw-text-muted: #9ca3af;
  --mw-border: #2a3a4a;
  --mw-bubble-in: #2a3a4a;`

export function styles({
  primaryColor,
  theme = 'light',
  position = 'bottom-right',
}: {
  primaryColor: string
  theme?: 'light' | 'dark' | 'auto'
  position?: 'bottom-right' | 'bottom-left'
}): string {
  // Base tokens applied vždy — light je default. Dark přepíše per-theme block.
  const baseTokens = theme === 'dark' ? DARK_TOKENS : LIGHT_TOKENS
  const colorScheme = theme === 'dark' ? 'dark' : theme === 'auto' ? 'light dark' : 'light'

  // Position-aware tokens — bubble + pane jsou flush k odpovídající straně.
  const horizontalSide = position === 'bottom-left' ? 'left' : 'right'
  const alignItems = position === 'bottom-left' ? 'flex-start' : 'flex-end'
  const transformOrigin = position === 'bottom-left' ? 'bottom left' : 'bottom right'

  // Pro 'auto' přidáme @media block co přepíše tokeny v dark mode.
  const autoOverride =
    theme === 'auto'
      ? `
@media (prefers-color-scheme: dark) {
  :host, .mw-root {${DARK_TOKENS}
  }
}`
      : ''

  return `
:host, .mw-root {
  --mw-primary: ${primaryColor};
  --mw-primary-fg: #ffffff;
  ${baseTokens}
  font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--mw-text);
  color-scheme: ${colorScheme};
}
${autoOverride}

.mw-root {
  position: fixed;
  bottom: 24px;
  ${horizontalSide}: 24px;
  z-index: 2147483647;
  display: flex;
  flex-direction: column;
  align-items: ${alignItems};
  gap: 12px;
  pointer-events: none; /* pane + bubble enable pointer-events individually */
}

.mw-bubble {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--mw-primary);
  color: var(--mw-primary-fg);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  transition: transform 0.15s ease;
  pointer-events: auto;
  position: relative;
}
.mw-bubble:hover { transform: scale(1.05); }
.mw-bubble:active { transform: scale(0.95); }
.mw-bubble-icon { transition: opacity 0.15s ease, transform 0.15s ease; }
.mw-bubble-icon[hidden] { display: none; }

.mw-pane {
  width: 380px;
  max-width: calc(100vw - 48px);
  height: 600px;
  max-height: calc(100vh - 110px);
  background: var(--mw-bg);
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--mw-border);
  pointer-events: auto;
  transform-origin: ${transformOrigin};
  animation: mw-pane-in 180ms cubic-bezier(0.16, 1, 0.3, 1);
}
.mw-pane[hidden] { display: none; }
@keyframes mw-pane-in {
  from { opacity: 0; transform: scale(0.94) translateY(8px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.mw-header {
  background: var(--mw-primary);
  color: var(--mw-primary-fg);
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.mw-title { font-weight: 600; font-size: 16px; }
.mw-subtitle { opacity: 0.85; font-size: 12px; margin-top: 2px; }
.mw-office-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.18);
}
.mw-office-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.mw-office-online .mw-office-dot { background: #34d399; box-shadow: 0 0 0 2px rgba(52, 211, 153, 0.2); }
.mw-office-offline .mw-office-dot { background: #9ca3af; }
.mw-close {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  opacity: 0.8;
}
.mw-close:hover { opacity: 1; background: rgba(255,255,255,0.15); }

.mw-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mw-msg {
  display: flex;
  flex-direction: column;
  max-width: 80%;
}
.mw-msg-inbound { align-self: flex-end; align-items: flex-end; }
.mw-msg-outbound { align-self: flex-start; align-items: flex-start; }

.mw-msg-bubble {
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 14px;
  word-wrap: break-word;
  white-space: pre-wrap;
}
.mw-msg-inbound .mw-msg-bubble {
  background: var(--mw-primary);
  color: var(--mw-primary-fg);
  border-bottom-right-radius: 4px;
}
.mw-msg-outbound .mw-msg-bubble {
  background: var(--mw-bubble-in);
  color: var(--mw-text);
  border-bottom-left-radius: 4px;
}

/* PLA-346 — bezpečný markdown subset v AI bublinách (Shadow DOM nemá Tailwind). */
.mw-msg-bubble-md { white-space: normal; }
.mw-msg-bubble-md p { margin: 0; }
.mw-msg-bubble-md p + p { margin-top: 6px; }
.mw-msg-bubble-md strong { font-weight: 600; }
.mw-msg-bubble-md em { font-style: italic; }
.mw-msg-bubble-md ul,
.mw-msg-bubble-md ol { margin: 6px 0; padding-left: 20px; }
.mw-msg-bubble-md li { margin: 2px 0; }
.mw-msg-bubble-md a {
  color: var(--mw-primary);
  text-decoration: underline;
  word-break: break-word;
}

.mw-msg-meta {
  font-size: 11px;
  color: var(--mw-text-muted);
  margin-top: 4px;
}

.mw-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px 8px;
  height: 20px;
  box-sizing: content-box;
  visibility: hidden;
  transition: visibility 0s linear 0s;
}
/* Tečky drží rezervované místo i když nejsou viditelné — žádný layout shift. */
.mw-typing:not([hidden]) {
  visibility: visible;
}
.mw-typing-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--mw-text-muted);
  animation: mw-typing-bounce 1.4s infinite ease-in-out;
}
.mw-typing-dot:nth-child(2) { animation-delay: 0.15s; }
.mw-typing-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes mw-typing-bounce {
  0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
}

.mw-composer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid var(--mw-border);
}
.mw-input {
  flex: 1;
  border: 1px solid var(--mw-border);
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  resize: none;
  background: var(--mw-bg);
  color: var(--mw-text);
  outline: none;
  min-height: 36px;
  max-height: 120px;
}
.mw-input:focus { border-color: var(--mw-primary); }
.mw-send {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--mw-primary);
  color: var(--mw-primary-fg);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;
}
.mw-send:hover { opacity: 0.9; }
.mw-send:disabled { opacity: 0.5; cursor: not-allowed; }
`
}
